/**
 * Whitelabel Configuration
 * Customer-specific branding and feature configuration
 */

export interface WhitelabelConfig {
  // Branding
  appName: string
  primaryColor: string
  secondaryColor?: string
  
  // Asset paths (relative to whitelabel/assets/)
  logoUrl?: string
  logoSmallUrl?: string
  faviconUrl?: string
  
  // Feature toggles
  features: {
    showLeaderboard: boolean
    allowUserSwitching: boolean
    allowLanguageSwitching: boolean
    showProfilePage: boolean
    enableNotifications: boolean
  }
  
  // Customization
  customCSS?: string
  footerText?: string
  
  // Home page customization
  homePageConfig?: {
    showMonthlyProgress?: boolean
    monthlyPointsGained?: number
  }
}

/**
 * Default whitelabel configuration
 * This serves as a template and fallback configuration
 */
export const WHITELABEL_CONFIG: WhitelabelConfig = {
  // Branding
  appName: "Rewards App",
  primaryColor: "#3880ff", // Ionic default blue
  secondaryColor: "#32db64", // Ionic default green
  
  // Asset paths (will resolve to /whitelabel/assets/ in build)
  logoUrl: "logo.png",
  logoSmallUrl: "logo-small.png", 
  faviconUrl: "favicon.ico",
  
  // Feature toggles (customize per customer)
  features: {
    showLeaderboard: true,
    allowUserSwitching: true,
    allowLanguageSwitching: true,
    showProfilePage: true,
    enableNotifications: true
  },
  
  // Customization
  customCSS: "", // Path to custom CSS file or inline styles
  footerText: "© 2024 Rewards App. All rights reserved.",
  
  // Home page customization
  homePageConfig: {
    showMonthlyProgress: true,
    monthlyPointsGained: 1350
  }
}

/**
 * Get asset URL for whitelabel assets
 * Handles both development and production asset resolution with fallbacks
 */
export function getWhitelabelAssetUrl(assetPath: string): string {
  // In development, assets are served from /whitelabel/assets/
  // In production, assets are copied to /assets/whitelabel/
  const baseUrl = import.meta.env.DEV 
    ? '/whitelabel/assets/'
    : '/assets/whitelabel/'
    
  return `${baseUrl}${assetPath}`
}

/**
 * Get asset URL with basic fallback support
 * Returns fallback path if assetPath is undefined/empty, otherwise returns asset URL
 * Note: Does not check if asset files actually exist - assumes they are available
 */
export function getAssetUrlWithFallback(assetPath: string | undefined, fallbackPath: string): string {
  if (!assetPath) {
    return fallbackPath
  }
  
  return getWhitelabelAssetUrl(assetPath)
}

/**
 * Development mode fallback configuration
 * Provides default assets when customer assets are missing
 */
export const DEVELOPMENT_FALLBACK_CONFIG = {
  logoUrl: "logo.svg", // Fallback to SVG which is more likely to exist
  logoSmallUrl: "logo-small.svg",
  faviconUrl: "favicon.svg"
}

/**
 * Get configuration with development fallbacks applied
 */
export function getConfigWithFallbacks(): WhitelabelConfig {
  const config = { ...WHITELABEL_CONFIG }
  
  // In development mode, apply fallbacks for missing assets
  if (import.meta.env.DEV) {
    config.logoUrl = config.logoUrl || DEVELOPMENT_FALLBACK_CONFIG.logoUrl
    config.logoSmallUrl = config.logoSmallUrl || DEVELOPMENT_FALLBACK_CONFIG.logoSmallUrl
    config.faviconUrl = config.faviconUrl || DEVELOPMENT_FALLBACK_CONFIG.faviconUrl
  }
  
  return config
}

/**
 * Check whitelabel configuration for missing asset paths
 * Note: Does not verify if asset files actually exist
 */
export function validateAssetsAvailability(): { warnings: string[], hasIssues: boolean } {
  const warnings: string[] = []
  const config = WHITELABEL_CONFIG
  
  // Check for missing asset paths
  if (!config.logoUrl) {
    warnings.push('No logo URL configured, using development fallback')
  }
  
  if (!config.logoSmallUrl) {
    warnings.push('No small logo URL configured, using development fallback')
  }
  
  if (!config.faviconUrl) {
    warnings.push('No favicon URL configured, using development fallback')
  }
  
  // In development mode, provide guidance
  if (import.meta.env.DEV && warnings.length > 0) {
    warnings.push('Add your customer assets to /whitelabel/assets/ directory')
    warnings.push('Update WHITELABEL_CONFIG paths to point to your assets')
  }
  
  return {
    warnings,
    hasIssues: warnings.length > 0
  }
}

/**
 * Validate whitelabel configuration
 */
export function validateWhitelabelConfig(config: WhitelabelConfig): string[] {
  const errors: string[] = []
  
  if (!config.appName || config.appName.trim().length === 0) {
    errors.push('appName is required and cannot be empty')
  }
  
  if (!config.primaryColor) {
    errors.push('primaryColor is required')
  } else if (!/^#[0-9A-Fa-f]{6}$/.test(config.primaryColor)) {
    errors.push('primaryColor must be a valid hex color (e.g., #3880ff)')
  }
  
  if (config.secondaryColor && !/^#[0-9A-Fa-f]{6}$/.test(config.secondaryColor)) {
    errors.push('secondaryColor must be a valid hex color (e.g., #32db64)')
  }
  
  return errors
}

export default WHITELABEL_CONFIG